import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getAdminTracesByTriggerType, getAdminTrace, TraceSummary, TraceSpan } from '../../api';
import { AdminLayout } from '../../components/admin/AdminLayout';

export function AdminScaffolding() {
  const navigate = useNavigate();
  const { traceId } = useParams<{ traceId?: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [traces, setTraces] = useState<TraceSummary[]>([]);
  const [traceDetail, setTraceDetail] = useState<TraceSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 30;

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      if (traceId) {
        loadTraceDetail(traceId);
      } else {
        loadTraces();
      }
    }
  }, [isAuthenticated, user, traceId, page]);

  const loadTraces = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminTracesByTriggerType('world_creation', limit, page * limit);
      setTraces(data.traces);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load traces');
    } finally {
      setLoading(false);
    }
  };

  const loadTraceDetail = async (tid: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminTrace(tid);
      setTraceDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trace');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  // Trace Detail View - Show the call tree
  if (traceId && traceDetail) {
    return <TraceDetailView trace={traceDetail} />;
  }

  return (
    <AdminLayout
      title="World Creation Traces"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Scaffolding' }]}
      actions={<span className="text-dark-400">{total} total</span>}
    >
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {loading ? (
          <div className="text-center text-dark-400 py-8">Loading...</div>
        ) : traces.length === 0 ? (
          <div className="text-center text-dark-400 py-8">
            <p>No scaffolding traces found.</p>
            <p className="text-sm mt-2">Traces are only recorded in development mode.</p>
          </div>
        ) : (
          traces.map((trace) => (
            <Link
              key={trace.id}
              to={`/admin/scaffolding/${trace.id}`}
              className="block bg-dark-800 border border-dark-600 rounded-lg p-4 hover:border-dark-500 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <StatusBadge status={trace.status} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-dark-50 font-medium">
                        World Creation
                      </span>
                      <span className="text-dark-400 text-sm">
                        ({trace.totalCalls} agent calls)
                      </span>
                    </div>
                    {trace.triggerContent && (
                      <div className="text-dark-400 text-sm truncate max-w-xl">
                        {trace.triggerContent.slice(0, 100)}...
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm text-dark-400">
                  <span>{(trace.totalInputTokens + trace.totalOutputTokens).toLocaleString()} tokens</span>
                  <span>{trace.totalLatencyMs.toLocaleString()}ms</span>
                  <span className="w-40 text-right">{new Date(trace.startedAt).toLocaleString()}</span>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      {total > limit && (
        <div className="flex items-center justify-between mt-4">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary disabled:opacity-50">
            Previous
          </button>
          <span className="text-dark-400">Page {page + 1} of {Math.ceil(total / limit)}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total} className="btn-secondary disabled:opacity-50">
            Next
          </button>
        </div>
      )}
    </AdminLayout>
  );
}

function StatusBadge({ status }: { status: TraceSummary['status'] }) {
  const colors = {
    'completed': 'bg-green-500',
    'in_progress': 'bg-yellow-500 animate-pulse',
    'failed': 'bg-red-500',
  };
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors[status]}`} />;
}

function TraceDetailView({ trace }: { trace: TraceSummary }) {
  // Group spans by phase
  const phaseGroups = groupSpansByPhase(trace.spans || []);
  
  return (
    <AdminLayout
      title="World Creation Trace"
      breadcrumbs={[
        { label: 'Admin', to: '/admin' },
        { label: 'Scaffolding', to: '/admin/scaffolding' },
        { label: trace.id },
      ]}
      actions={<StatusBadge status={trace.status} />}
    >
      <div className="space-y-6">
        {/* Summary Card */}
        <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-dark-50 mb-4">Trace Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoItem label="Trigger" value="World Creation" />
            <InfoItem label="Total Calls" value={trace.totalCalls.toString()} />
            <InfoItem label="Total Tokens" value={(trace.totalInputTokens + trace.totalOutputTokens).toLocaleString()} />
            <InfoItem label="Total Latency" value={`${trace.totalLatencyMs.toLocaleString()}ms`} />
            <InfoItem label="Started" value={new Date(trace.startedAt).toLocaleString()} />
            <InfoItem label="Completed" value={trace.completedAt ? new Date(trace.completedAt).toLocaleString() : '-'} />
            <InfoItem label="Status" value={trace.status} />
            {trace.errorMessage && <InfoItem label="Error" value={trace.errorMessage} />}
          </div>
          {trace.triggerContent && (
            <div className="mt-4 pt-4 border-t border-dark-600">
              <div className="text-dark-400 text-sm mb-2">World Concept</div>
              <div className="bg-dark-700 rounded p-3 text-dark-200 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                {trace.triggerContent}
              </div>
            </div>
          )}
        </div>

        {/* Phase-Grouped Call Tree */}
        <div className="space-y-4">
          {phaseGroups.scaffolding.length > 0 && (
            <PhaseGroup 
              title="🏗️ SCAFFOLDING" 
              description="World creation agents"
              spans={phaseGroups.scaffolding}
              colorClass="border-purple-500/50"
            />
          )}
          {phaseGroups.preflight.length > 0 && (
            <PhaseGroup 
              title="🚀 PRE-FLIGHT" 
              description="Instant tasks before main inference"
              spans={phaseGroups.preflight}
              colorClass="border-blue-500/50"
            />
          )}
          {phaseGroups.processing.length > 0 && (
            <PhaseGroup 
              title="⚡ PROCESSING" 
              description="Main inference + quality checks"
              spans={phaseGroups.processing}
              colorClass="border-yellow-500/50"
            />
          )}
          {phaseGroups.postprocessing.length > 0 && (
            <PhaseGroup 
              title="📦 POST-PROCESSING" 
              description="Background tasks after response sent"
              spans={phaseGroups.postprocessing}
              colorClass="border-green-500/50"
            />
          )}
          {phaseGroups.unknown.length > 0 && (
            <PhaseGroup 
              title="❓ OTHER" 
              description="Uncategorized spans"
              spans={phaseGroups.unknown}
              colorClass="border-dark-500"
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

interface PhaseGroupData {
  scaffolding: TraceSpan[];
  preflight: TraceSpan[];
  processing: TraceSpan[];
  postprocessing: TraceSpan[];
  unknown: TraceSpan[];
}

function groupSpansByPhase(spans: TraceSpan[]): PhaseGroupData {
  const groups: PhaseGroupData = {
    scaffolding: [],
    preflight: [],
    processing: [],
    postprocessing: [],
    unknown: [],
  };
  
  for (const span of spans) {
    const phase = span.phaseGroup || inferPhaseFromAgent(span.agentName);
    groups[phase].push(span);
  }
  
  return groups;
}

function inferPhaseFromAgent(agentName: string): keyof PhaseGroupData {
  const lower = agentName.toLowerCase();
  
  // Scaffolding agents (world creation)
  if (lower.includes('genre_analyzer') || lower.includes('story_shaper') || 
      lower.includes('character_lister') || lower.includes('character_enricher') ||
      lower.includes('scaffolding')) {
    return 'scaffolding';
  }
  
  // Preflight agents
  if (lower.includes('classifier') || lower.includes('preflight') || lower.includes('reaction')) {
    return 'preflight';
  }
  
  // Processing agents
  if (lower.includes('generator') || lower.includes('narrative') || 
      lower.includes('validator') || lower.includes('guardrail') || 
      lower.includes('rewriter') || lower.includes('sculptor') ||
      lower.includes('pov') || lower.includes('normalizer')) {
    return 'processing';
  }
  
  // Postprocessing agents
  if (lower.includes('curator') || lower.includes('extractor') || 
      lower.includes('summarizer') || lower.includes('analyzer') ||
      lower.includes('enricher') || lower.includes('detector') ||
      lower.includes('compressor') || lower.includes('curation') ||
      lower.includes('sanitizer')) {
    return 'postprocessing';
  }
  
  return 'unknown';
}

function PhaseGroup({ 
  title, 
  description, 
  spans, 
  colorClass 
}: { 
  title: string; 
  description: string; 
  spans: TraceSpan[]; 
  colorClass: string;
}) {
  const [expanded, setExpanded] = useState(true);
  const totalTokens = spans.reduce((sum, s) => sum + s.inputTokens + s.outputTokens, 0);
  const totalLatency = spans.reduce((sum, s) => sum + s.latencyMs, 0);
  const spanTree = buildSpanTree(spans);
  
  return (
    <div className={`bg-dark-800 border ${colorClass} rounded-lg overflow-hidden`}>
      <button 
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-dark-700 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <span className="text-lg font-semibold text-dark-50">{title}</span>
          <span className="text-dark-400 text-sm">{description}</span>
          <span className="text-dark-400 text-sm">({spans.length} calls)</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-dark-400">
          <span>{totalTokens.toLocaleString()} tokens</span>
          <span>{totalLatency.toLocaleString()}ms</span>
          <span className="text-dark-500">{expanded ? '▼' : '▶'}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-6 pb-4 space-y-1">
          {spanTree.map((node) => (
            <SpanTreeNode key={node.span.spanId} node={node} />
          ))}
        </div>
      )}
    </div>
  );
}

interface SpanTreeNode {
  span: TraceSpan;
  children: SpanTreeNode[];
}

function buildSpanTree(spans: TraceSpan[]): SpanTreeNode[] {
  const nodeMap = new Map<string, SpanTreeNode>();
  const roots: SpanTreeNode[] = [];
  
  // Create all nodes
  for (const span of spans) {
    nodeMap.set(span.spanId, { span, children: [] });
  }
  
  // Build tree
  for (const span of spans) {
    const node = nodeMap.get(span.spanId)!;
    if (span.parentSpanId && nodeMap.has(span.parentSpanId)) {
      nodeMap.get(span.parentSpanId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  
  return roots;
}

function SpanTreeNode({ node, depth = 0 }: { node: SpanTreeNode; depth?: number }) {
  const [expanded, setExpanded] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const hasChildren = node.children.length > 0;
  const span = node.span;
  const hasInputOutput = span.systemPrompt || span.messagesJson || span.responseContent;
  
  return (
    <div>
      <div 
        className={`flex items-center gap-2 py-2 px-3 rounded hover:bg-dark-700 cursor-pointer ${
          !span.success ? 'bg-red-900/20' : ''
        }`}
        style={{ marginLeft: `${depth * 24}px` }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {/* Expand/collapse indicator */}
        <span className="w-4 text-dark-500">
          {hasChildren ? (expanded ? '▼' : '▶') : '•'}
        </span>
        
        {/* Agent name */}
        <span className="text-primary-400 font-medium min-w-[140px]">
          {span.agentName}
        </span>
        
        {/* Model */}
        <span className="text-dark-400 text-sm min-w-[200px]">
          {span.provider}/{span.model}
        </span>
        
        {/* Tokens */}
        <span className="text-dark-400 text-sm min-w-[100px]">
          {span.inputTokens + span.outputTokens} tok
        </span>
        
        {/* Latency */}
        <span className="text-dark-400 text-sm min-w-[80px]">
          {span.latencyMs}ms
        </span>
        
        {/* Status indicator */}
        <span className={`w-2 h-2 rounded-full ${span.success ? 'bg-green-500' : 'bg-red-500'}`} />
        
        {/* Error */}
        {span.errorCode && (
          <span className="text-red-400 text-sm ml-2">
            {span.errorCode}
          </span>
        )}
        
        {/* Detail toggle button */}
        {hasInputOutput && (
          <button 
            className="ml-auto text-primary-400 hover:text-primary-300 text-sm"
            onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }}
          >
            {showDetails ? 'Hide' : 'View'} Details
          </button>
        )}
      </div>
      
      {/* Input/Output Details Panel */}
      {showDetails && hasInputOutput && (
        <SpanDetailsPanel span={span} depth={depth} />
      )}
      
      {/* Children */}
      {expanded && node.children.map((child) => (
        <SpanTreeNode key={child.span.spanId} node={child} depth={depth + 1} />
      ))}
    </div>
  );
}

function SpanDetailsPanel({ span, depth }: { span: TraceSpan; depth: number }) {
  const [activeTab, setActiveTab] = useState<'system' | 'messages' | 'response' | 'output'>('messages');
  
  // Parse messages JSON
  let messages: Array<{ role: string; content: string }> = [];
  try {
    if (span.messagesJson) {
      messages = JSON.parse(span.messagesJson);
    }
  } catch {
    // Invalid JSON
  }
  
  // Try to parse response as JSON for "Output" tab
  let parsedOutput: unknown = null;
  let hasStructuredOutput = false;
  try {
    if (span.responseContent) {
      const jsonMatch = span.responseContent.match(/```(?:json)?\s*([\s\S]*?)```/) ||
                        span.responseContent.match(/(\{[\s\S]*\})/) ||
                        span.responseContent.match(/(\[[\s\S]*\])/);
      const jsonStr = jsonMatch ? jsonMatch[1] : span.responseContent;
      parsedOutput = JSON.parse(jsonStr.trim());
      hasStructuredOutput = true;
    }
  } catch {
    // Not valid JSON
  }
  
  return (
    <div 
      className="bg-dark-700 border border-dark-600 rounded-lg mt-1 mb-2 overflow-hidden"
      style={{ marginLeft: `${(depth + 1) * 24 + 16}px` }}
    >
      {/* Tab Header */}
      <div className="flex border-b border-dark-600">
        {span.systemPrompt && (
          <button
            className={`px-4 py-2 text-sm ${activeTab === 'system' ? 'bg-dark-600 text-dark-50' : 'text-dark-400 hover:text-dark-50'}`}
            onClick={() => setActiveTab('system')}
          >
            System Prompt
          </button>
        )}
        {messages.length > 0 && (
          <button
            className={`px-4 py-2 text-sm ${activeTab === 'messages' ? 'bg-dark-600 text-dark-50' : 'text-dark-400 hover:text-dark-50'}`}
            onClick={() => setActiveTab('messages')}
          >
            Messages ({messages.length})
          </button>
        )}
        {span.responseContent && (
          <button
            className={`px-4 py-2 text-sm ${activeTab === 'response' ? 'bg-dark-600 text-dark-50' : 'text-dark-400 hover:text-dark-50'}`}
            onClick={() => setActiveTab('response')}
          >
            Response
          </button>
        )}
        {hasStructuredOutput && (
          <button
            className={`px-4 py-2 text-sm ${activeTab === 'output' ? 'bg-dark-600 text-dark-50' : 'text-dark-400 hover:text-dark-50'}`}
            onClick={() => setActiveTab('output')}
          >
            Output
          </button>
        )}
      </div>
      
      {/* Tab Content */}
      <div className="p-4 max-h-96 overflow-y-auto">
        {activeTab === 'system' && span.systemPrompt && (
          <pre className="text-dark-200 text-sm whitespace-pre-wrap font-mono">{span.systemPrompt}</pre>
        )}
        
        {activeTab === 'messages' && messages.length > 0 && (
          <div className="space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`rounded p-3 ${
                msg.role === 'user' ? 'bg-dark-600' : 
                msg.role === 'assistant' ? 'bg-primary-900/30' : 
                'bg-dark-800'
              }`}>
                <div className="text-xs text-dark-400 mb-1 font-semibold uppercase">{msg.role}</div>
                <pre className="text-dark-200 text-sm whitespace-pre-wrap font-mono">
                  {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'response' && span.responseContent && (
          <pre className="text-dark-200 text-sm whitespace-pre-wrap font-mono">{span.responseContent}</pre>
        )}
        
        {activeTab === 'output' && hasStructuredOutput && (
          <pre className="text-dark-200 text-sm whitespace-pre-wrap font-mono bg-dark-800 rounded p-3">
            {JSON.stringify(parsedOutput, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-dark-400 text-sm">{label}</div>
      <div className="text-dark-50 font-medium">{value}</div>
    </div>
  );
}
